/**
 * @file OwnerChip.tsx
 * @description Стикер «чей ход» — одна форма на всю систему заказов
 * (phase19.24-order-card-chain-design.md §14).
 *
 * Одна и та же наклейка живёт в полосе «Сейчас», под точкой степпера, в
 * карточке реестра и на канбане: менеджер узнаёт «мой ход» по форме и цвету,
 * не вчитываясь. Цвет никогда не единственный признак — у стикера всегда есть
 * текст и иконка lucide (эмодзи в интерфейсе запрещены).
 *
 * Что показывать, решает реестр `lib/order-chain-owner.ts`: компонент только
 * рисует. Поэтому новый владелец хода (мастер маркетплейса на этапе 2) не
 * требует правок этого файла.
 */

import type { OrderChainOwner } from "../../lib/order-chain";
import {
  ORDER_CHAIN_ALERT_META,
  ownerMeta,
  type OrderChainAlert,
} from "../../lib/order-chain-owner";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";

type Props = {
  /** Короткая подпись («вы», «склад») вместо полной — под точкой степпера. */
  short?: boolean;
  /** Своя подпись: «Просрочено · 2 дн». */
  label?: string;
  /**
   * Контурный вариант: стикер рядом с красным («Ваш ход» при проблеме) не
   * дублирует тревогу заливкой — правило «не больше двух стикеров» из §14.
   */
  outlined?: boolean;
  /** Подсказка вместо роли: «недостача при сборке» у стикера проблемы. */
  title?: string;
  className?: string;
} & (
  | { owner: OrderChainOwner | null | undefined; alert?: never }
  /** Красный стикер поверх владельца: просрочка или проблема. */
  | { alert: OrderChainAlert; owner?: never }
);

export function OwnerChip({
  short = false,
  label,
  outlined = false,
  title,
  className,
  ...rest
}: Props) {
  const meta = "alert" in rest && rest.alert
    ? ORDER_CHAIN_ALERT_META[rest.alert]
    : ownerMeta("owner" in rest ? rest.owner : null);
  const Icon = meta.icon;
  const text = label ?? (short ? meta.short : meta.label);

  return (
    <Badge
      className={cn("gap-1", outlined ? meta.chipOutline : meta.chip, className)}
      title={title ?? meta.aria}
      // Роль читается текстом, иконка — декоративная.
      aria-label={meta.aria}
    >
      <Icon aria-hidden />
      {text}
    </Badge>
  );
}
